import { createContext, useContext } from 'react'
import { useGetAllScanFindingPages } from '../../../utils/api-client/api-hooks.ts'
import type { FindingResponse, Severity } from '../../../utils/api-client/api-schemas.ts'

/**
 * The scan's platform findings, which the coverage views list in place of the ids the summary carries:
 * a Beacon finding's id on the platform is the SARIF result guid, which is the summary's `finding_uuid`.
 */
export type VerifiedFindingLookup = {
  byId: ReadonlyMap<string, FindingResponse>
  onOpen: (findingId: string) => unknown
}

export const VerifiedFindingsContext = createContext<VerifiedFindingLookup | undefined>(undefined)

export const useVerifiedFindingLookup = () => useContext(VerifiedFindingsContext)

/** Every finding of the scan, fetched once for all the coverage views, each opened by `onOpen`. */
export const useScanVerifiedFindings = ({
  scanId,
  onOpen,
}: {
  scanId?: string
  onOpen: (findingId: string) => unknown
}): VerifiedFindingLookup => {
  const findings = useGetAllScanFindingPages({ scanId }).flatMap(page => page.data?._embedded.items ?? [])
  return { byId: new Map(findings.map(finding => [finding.id, finding])), onOpen }
}

export type VerifiedFindingRow = {
  id: string
  title: string
  severity?: Severity
  onOpen: () => unknown
}

/**
 * The rows to list for a part's findings, most severe first. Each is a platform finding that opens like
 * any other; an id the scan does not have is left out rather than shown bare.
 */
export const verifiedFindingRows = ({
  findingIds,
  lookup,
}: {
  findingIds: readonly string[]
  lookup?: VerifiedFindingLookup
}): VerifiedFindingRow[] =>
  findingIds
    .flatMap((id): VerifiedFindingRow[] => {
      const finding = lookup?.byId.get(id)
      return finding && lookup
        ? [{ id, title: finding.title ?? finding.rule, severity: finding.severity, onOpen: () => lookup.onOpen(id) }]
        : []
    })
    .sort((a, b) => (b.severity?.rank ?? 0) - (a.severity?.rank ?? 0))
